import * as React from 'react';

import { Button, EntityDetails, Menu, useToast } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Save,
  X,
  Play,
  Pause,
  MoreHorizontal,
  Trash2,
  Copy,
} from 'lucide-react';
import { useLocation } from 'wouter';

import {
  $editorAutomationId,
  $remoteEditorAutomation,
  $hasUnsavedChanges,
  $saveFlow,
  $initializeEditor,
  $resetEditor,
  $editorNodes,
  $editorEdges,
  $isCreateMode,
  $automationName,
  $automationDescription,
  $createAutomationTriggerType,
  $createAutomation,
  $resetCreateMode,
  $editorTab,
  $editorInitializedForId,
} from '../../_state/automationEditor.state';
import { useTranslations } from '../../i18n/useTranslations';
import { automationsRoutes } from '../../routes';
import { TriggerType } from '../../types';
import { DeleteAutomationDialog } from '../dialogs/DeleteAutomationDialog';
import { TestPayloadDialog } from '../dialogs/TestPayloadDialog';
import { FlowTabContent } from '../editor/FlowTabContent';
import { HistoryTabContent } from '../editor/HistoryTabContent';
import { isTriggerNodeType } from '../editor/nodes/types';
import { VersionHistoryTabContent } from '../editor/VersionHistoryTabContent';

import { useAutomationActions } from './hooks/useAutomationActions';

export const AutomationEditorView: React.FC = () => {
  const t = useTranslations();
  const [, navigate] = useLocation();
  const toast = useToast();

  const isCreateMode = useAtomValue($isCreateMode);

  const automationId = useAtomValue($editorAutomationId);
  const automationQuery = useAtomValue($remoteEditorAutomation);
  const automation = automationQuery.data;
  const saveFlow = useSetAtom($saveFlow);
  const initializeEditor = useSetAtom($initializeEditor);

  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);
  const resetEditor = useSetAtom($resetEditor);
  const nodes = useAtomValue($editorNodes);
  const edges = useAtomValue($editorEdges);
  const [editorTab, setEditorTab] = useAtom($editorTab);

  const automationName = useAtomValue($automationName);
  const automationDescription = useAtomValue($automationDescription);
  const triggerType = useAtomValue($createAutomationTriggerType);
  const createAutomationMutation = useSetAtom($createAutomation);
  const resetCreateMode = useSetAtom($resetCreateMode);
  const editorInitializedForId = useAtomValue($editorInitializedForId);

  const [showUnsavedWarning, setShowUnsavedWarning] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showTestDialog, setShowTestDialog] = React.useState(false);
  const [isTesting, _setIsTesting] = React.useState(false);

  const handleDeleteSuccess = React.useCallback(() => {
    navigate(automationsRoutes.list);
  }, [navigate]);

  const {
    handleActivate,
    handlePause,
    handleDelete,
    handleDuplicate,
    handleTest,
  } = useAutomationActions({
    automationId,
    onDeleteSuccess: handleDeleteSuccess,
  });

  const hasTriggerNode = React.useMemo(
    () => nodes.some((node) => node.type && isTriggerNodeType(node.type)),
    [nodes],
  );

  const triggerNodeInfo = React.useMemo(() => {
    const triggerNode = nodes.find(
      (node) => node.type && isTriggerNodeType(node.type),
    );
    if (!triggerNode) return null;
    return {
      type: triggerNode.type as
        | 'BusinessEventTrigger'
        | 'HttpWebhookTrigger'
        | 'ScheduleTrigger',
      config: (triggerNode.data?.config as Record<string, unknown>) || {},
    };
  }, [nodes]);

  React.useEffect(() => {
    resetEditor();
    if (isCreateMode) {
      resetCreateMode();
    }
  }, [automationId, isCreateMode, resetEditor, resetCreateMode]);

  React.useEffect(() => {
    if (
      !isCreateMode &&
      automationQuery.data &&
      editorInitializedForId !== automationId
    ) {
      initializeEditor();
    }
  }, [
    isCreateMode,
    automationQuery.data,
    initializeEditor,
    editorInitializedForId,
    automationId,
  ]);

  const handleSave = React.useCallback(async () => {
    if (isCreateMode) {
      if (!triggerType) {
        toast.error(t.form.validation.triggerTypeRequired);
        return;
      }

      const triggerNode = nodes.find(
        (n) => n.type && isTriggerNodeType(n.type),
      );
      const triggerConfig =
        (triggerNode?.data?.config as Record<string, unknown>) || {};

      try {
        setIsSubmitting(true);

        const result = await createAutomationMutation({
          name: automationName.trim() || t.editor.create.namePlaceholder,
          description: automationDescription || null,
          nodes,
          edges,
          triggerType: triggerType as TriggerType,
          triggerConfig,
        });

        toast.success(t.detail.messages.created);

        const newId = result?.automationsAutomationCreate?.id;
        if (newId) {
          navigate(automationsRoutes.detail.replace(':id', newId));
        } else {
          navigate(automationsRoutes.list);
        }
      } catch {
        toast.error(t.detail.messages.createFailed);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!automationId) return;

      try {
        setIsSubmitting(true);
        await saveFlow({
          id: automationId,
          name: automationName,
          description: automationDescription || null,
          nodes,
          edges,
        });
        toast.success(t.detail.messages.saved);
      } catch {
        toast.error(t.detail.messages.saveFailed);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [
    isCreateMode,
    triggerType,
    nodes,
    edges,
    automationName,
    automationDescription,
    createAutomationMutation,
    automationId,
    saveFlow,
    toast,
    t,
    navigate,
  ]);

  const handleBack = React.useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      navigate(automationsRoutes.list);
    }
  }, [hasUnsavedChanges, navigate]);

  const handleConfirmLeave = React.useCallback(() => {
    setShowUnsavedWarning(false);
    navigate(automationsRoutes.list);
  }, [navigate]);

  const handleCancelLeave = React.useCallback(() => {
    setShowUnsavedWarning(false);
  }, []);

  const handleDeleteClick = React.useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = React.useCallback(async () => {
    setIsDeleting(true);
    try {
      await handleDelete();
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  }, [handleDelete]);

  const handleTestClick = React.useCallback(() => {
    setShowTestDialog(true);
  }, []);

  const handleTestConfirm = React.useCallback(
    (payload: Record<string, unknown>) => {
      setShowTestDialog(false);
      handleTest(payload);
    },
    [handleTest],
  );

  const menuItems = React.useMemo(
    () => [
      {
        id: 'duplicate',
        label: t.detail.actions.duplicate,
        icon: <Copy className="size-4" />,
        onPress: handleDuplicate,
      },
      {
        id: 'delete',
        label: t.detail.actions.delete,
        icon: <Trash2 className="size-4" />,
        onPress: handleDeleteClick,
        color: 'failure' as const,
      },
    ],
    [handleDuplicate, handleDeleteClick, t],
  );

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const expectedId = isCreateMode ? 'create' : automationId;
  if (editorInitializedForId !== expectedId) {
    return (
      <div className="@AutomationEditorView flex h-full items-center justify-center">
        <p className="text-neutral/60">{t.detail.loading}</p>
      </div>
    );
  }

  if (!isCreateMode && automationQuery.isLoading) {
    return (
      <div className="@AutomationEditorView flex h-full items-center justify-center">
        <p className="text-neutral/60">{t.detail.loading}</p>
      </div>
    );
  }

  if (!isCreateMode && (automationQuery.error || !automationQuery.data)) {
    return (
      <div className="@AutomationEditorView flex h-full flex-col items-center justify-center gap-4">
        <p className="text-failure">{t.detail.loadFailed}</p>
        <Button
          variant="outline"
          onPress={() => navigate(automationsRoutes.list)}
        >
          {t.detail.goBack}
        </Button>
      </div>
    );
  }

  const headerTitle = isCreateMode
    ? t.detail.newAutomation
    : automationName || automation?.name || t.detail.untitledAutomation;

  const saveButtonLabel = isCreateMode
    ? t.editor.create.saveButton
    : t.editor.actions.save;

  const saveButtonDisabled = isCreateMode
    ? !hasTriggerNode || isSubmitting
    : !hasUnsavedChanges || isSubmitting;

  const headerActions = (
    <div className="flex items-center gap-2">
      {!isCreateMode && automation && (
        <>
          <Button
            variant="outline"
            icon={<Play className="size-4" />}
            onPress={handleTestClick}
          >
            {t.detail.actions.test}
          </Button>
          {automation.status === 'Active' && (
            <Button
              variant="outline"
              color="warning"
              icon={<Pause className="size-4" />}
              onPress={handlePause}
            >
              {t.detail.actions.pause}
            </Button>
          )}
          {(automation.status === 'Draft' ||
            automation.status === 'Paused') && (
            <Button
              variant="outline"
              color="success"
              icon={<Play className="size-4" />}
              onPress={handleActivate}
            >
              {t.detail.actions.activate}
            </Button>
          )}
          <Menu
            trigger={
              <Button
                icon={<MoreHorizontal className="size-4" />}
                variant="outline"
                className="w-8"
              />
            }
            items={menuItems}
          />
        </>
      )}
      <Button
        variant="outline"
        onPress={handleBack}
        icon={<X className="size-4" />}
        isDisabled={isSubmitting}
      >
        {t.editor.actions.cancel}
      </Button>
      <Button
        variant="solid"
        color="primary"
        onPress={handleSave}
        icon={<Save className="size-4" />}
        isDisabled={saveButtonDisabled}
        isLoading={isSubmitting}
      >
        {saveButtonLabel}
      </Button>
    </div>
  );

  const subtitle = hasUnsavedChanges ? (
    <span className="text-warning">{t.detail.unsavedChanges}</span>
  ) : undefined;

  return (
    <>
      <EntityDetails
        className="@AutomationEditorView"
        onBackPressed={handleBack}
        primaryTitle={headerTitle}
        subtitle={subtitle}
        headerActions={headerActions}
        currentTab={editorTab}
        onChangeTab={(tab) =>
          setEditorTab(tab as 'flow' | 'history' | 'versions')
        }
        tabs={{
          flow: {
            title: t.detail.tabs.flow,
            content: FlowTabContent,
          },
          history: {
            title: t.detail.tabs.executions,
            content: HistoryTabContent,
          },
          ...(isCreateMode
            ? {}
            : {
                versions: {
                  title: t.detail.tabs.versions,
                  content: VersionHistoryTabContent,
                },
              }),
        }}
      />

      {showUnsavedWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral/40">
          <div className="w-full max-w-lg min-w-[32rem] rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold">
              {t.detail.unsavedChanges}
            </h3>
            <p className="mb-6 text-neutral/70">
              {t.editor.messages.unsavedChanges}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onPress={handleCancelLeave}>
                {t.detail.stay}
              </Button>
              <Button
                variant="solid"
                color="failure"
                onPress={handleConfirmLeave}
              >
                {t.detail.leaveWithoutSaving}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!isCreateMode && automation && (
        <DeleteAutomationDialog
          isOpen={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          automationName={automation.name}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />
      )}

      {!isCreateMode && triggerNodeInfo && (
        <TestPayloadDialog
          isOpen={showTestDialog}
          onOpenChange={setShowTestDialog}
          triggerType={triggerNodeInfo.type}
          triggerConfig={triggerNodeInfo.config}
          onConfirm={handleTestConfirm}
          isTesting={isTesting}
        />
      )}
    </>
  );
};
